import React, { useState, useEffect } from 'react';
import { fetchActivityLogs, verifyAdmin } from '../../services/adminService';
import { useAdminAuth } from '../../hooks/useAdminAuth';

function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAdmin, setFilterAdmin] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [error, setError] = useState('');
  const { isAdmin } = useAdminAuth();

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Verify admin access
      const adminStatus = await verifyAdmin();
      if (!adminStatus.isAdmin) {
        throw new Error('Unauthorized: Admin access required');
      }
      
      const allLogs = await fetchActivityLogs({ limit: 100 });
      setLogs(allLogs);
    } catch (error) {
      console.error('Error loading activity logs:', error);
      setError(error.message || 'Failed to load activity logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesAdmin = filterAdmin === 'all' || log.adminId === filterAdmin;
    const matchesAction = filterAction === 'all' || log.action === filterAction;
    return matchesAdmin && matchesAction;
  });

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }
    return new Date(timestamp).toLocaleString();
  };

  const getUniqueAdmins = () => {
    const admins = [...new Set(logs.map(log => log.adminEmail).filter(Boolean))];
    return admins;
  };

  const getUniqueActions = () => {
    const actions = [...new Set(logs.map(log => log.action).filter(Boolean))];
    return actions;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show error if unauthorized
  if (!isAdmin) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Unauthorized: Admin access required
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="bi bi-clock-history me-2"></i>
          Activity Log
        </h2>
        <button className="btn btn-primary" onClick={loadLogs} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setError('')}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label htmlFor="adminFilter" className="form-label">Filter by Admin</label>
              <select
                className="form-select"
                id="adminFilter"
                value={filterAdmin}
                onChange={(e) => setFilterAdmin(e.target.value)}
              >
                <option value="all">All Admins</option>
                {getUniqueAdmins().map((admin, index) => (
                  <option key={index} value={admin}>{admin}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label htmlFor="actionFilter" className="form-label">Filter by Action</label>
              <select
                className="form-select"
                id="actionFilter"
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
              >
                <option value="all">All Actions</option>
                {getUniqueActions().map((action, index) => (
                  <option key={index} value={action}>{action}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Target User</th>
                  <th>Changes</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-muted">
                      No activity logs found
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id}>
                      <td>{formatTimestamp(log.timestamp)}</td>
                      <td>{log.adminEmail || 'N/A'}</td>
                      <td>
                        <span className="badge bg-info">{log.action || 'N/A'}</span>
                      </td>
                      <td>{log.targetUserEmail || log.targetUserId || 'N/A'}</td>
                      <td>
                        {log.changes && Object.keys(log.changes).length > 0 ? (
                          <details>
                            <summary className="text-primary" style={{ cursor: 'pointer' }}>
                              View Changes ({Object.keys(log.changes).length})
                            </summary>
                            <div className="mt-2 small">
                              {Object.entries(log.changes).map(([key, change]) => (
                                <div key={key} className="mb-1">
                                  <strong>{key}:</strong> {JSON.stringify(change.before)} → {JSON.stringify(change.after)}
                                </div>
                              ))}
                            </div>
                          </details>
                        ) : (
                          'No changes'
                        )}
                      </td>
                      <td>{log.reason || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-muted">
            Showing {filteredLogs.length} of {logs.length} logs
          </div>
        </div>
      </div>
    </div>
  );
}

export default ActivityLog;

